import dayjs from 'dayjs';
import { memo, useMemo, useState } from 'react';

import { Badge, CustomAlert, CustomButton } from '../../shared/uiComponents';
import { GreenDot, InfoFilled, OrangeDot, RedDot } from '../../shared/Assets/svgIcons';
import { HeaderProperties } from '../../shared/uiComponents/Table/tableProperties';
import { StatusInfoProps } from './types';
import { toClientDate } from '../../shared/Helpers/timezones';

export const ExpireInfo = memo(
	({ endDate, startDate, timeZoneId }: { endDate: string; startDate?: string; timeZoneId?: string }) => {
		const daysDiff = useMemo(() => dayjs(endDate).diff(dayjs(), 'days'), [endDate]);

		if (startDate && dayjs(startDate).isAfter(dayjs(toClientDate(new Date(), timeZoneId)), 'day')) {
			return <Badge label="Upcoming" type="primary" sizeType="sm" />;
		}

		if (daysDiff < 0) {
			return <Badge label="Expired" type="error" sizeType="sm" />;
		}

		if (daysDiff <= 30) {
			return <Badge label={`${daysDiff + 1} Day${daysDiff > 0 ? 's' : ''}`} type="warning" sizeType="sm" />;
		}

		return <Badge label="Active" type="success" sizeType="sm" />;
	},
);

ExpireInfo.displayName = 'ExpireInfo';

export const findExpiredInfoColor = ({ endDate }: { endDate: string }) => {
	if (dayjs(endDate).diff(dayjs(), 'days') < 0) return 'error';
	if (dayjs(endDate).diff(dayjs(), 'M') < 3) return 'warning';

	return 'success';
};

export const AuthorizationStatusInfo = memo(({ showExpired = false }: StatusInfoProps) => {
	const [open, setOpen] = useState<boolean>(false);

	const alertContent = useMemo(
		() => (
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: 8,
					width: 'max-content',
				}}
			>
				{showExpired && <Badge label="Expired" type="error" sizeType="lg" startIcon={<RedDot />} />}
				<Badge label="Expires in 3 month" type="warning" sizeType="lg" startIcon={<OrangeDot />} />
				<Badge label="Active" type="success" sizeType="lg" startIcon={<GreenDot />} />
			</div>
		),
		[showExpired],
	);

	return (
		<>
			<CustomButton buttonType="secondaryGray" iconOnly={<InfoFilled />} onClick={() => setOpen(true)} />
			<CustomAlert
				open={open}
				onClose={() => setOpen(false)}
				title="Details for authorization period"
				Content={() => alertContent}
			/>
		</>
	);
});

AuthorizationStatusInfo.displayName = 'AuthorizationStatusInfo';

// Shared table headers
export const AUTHORIZATION_HEADERS: HeaderProperties[] = [
	{ id: '1', name: 'Client Name' },
	{ id: '2', name: 'Authorization Expiration Date' },
];

export const TEMPORARY_AUTHORIZATION_HEADERS: HeaderProperties[] = [
	{ id: '1', name: 'Client Name' },
	{ id: '3', name: 'Authorization Expiration Date' },
	{ id: '4', name: '' },
];

export const BCBA_AUTHORIZATION_HEADERS: HeaderProperties[] = [
	{ id: '1', name: 'Client' },
	{ id: '2', name: 'Authorization Period' },
	{ id: '3', name: '' },
];

export const FLAGGED_SESSIONS_HEADERS: HeaderProperties[] = [
	{ id: '8', name: 'Provider Name', orderBy: 'user.fullName' },
	{ id: '1', name: 'Client Name', orderBy: 'client.fullName' },
	{ id: '2', name: 'Session Type', orderBy: 'sessionType.name' },
	{ id: '3', name: 'Session Date', orderBy: 'date' },
	{ id: '6', name: 'Report Status' },
	{ id: '7', name: '' },
];

export const SESSION_STATUSES = {
	completed: { label: 'Completed', className: 'completed' },
	inProgress: { label: 'In Progress', className: 'inprogress' },
	upcoming: { label: 'Upcoming', className: 'upcoming' },
} as const;

export const getSessionStatus = (session: { isCompleted: boolean; sessionId: string | null }) => {
	if (session.isCompleted) return SESSION_STATUSES.completed;
	if (session.sessionId) return SESSION_STATUSES.inProgress;
	return SESSION_STATUSES.upcoming;
};

export const getSessionButtonProps = (opts: {
	isCompleted: boolean;
	sessionId: string | null;
	canSkipSession: boolean;
}) => {
	if (opts.isCompleted) return { title: 'Session Ended', disabled: true };
	if (opts.sessionId || opts.canSkipSession) return { title: 'Collect Data', disabled: false };
	return { title: 'Start Session', disabled: false };
};

// Shared dashboard theme colors (keep in sync with $DASH-* SCSS variables in index.scss)
export const DASH_COLORS = {
	teal: '#55aeb3',
	purple: '#a56aba',
	indigo: '#615fff',
	orange: '#e0944a',
	red: '#d94f4f',
	green: '#5aab72',
	blue: '#6ea8d8',
	blueSoft: '#7b8cde',
} as const;

export const AVATAR_COLORS = [
	DASH_COLORS.teal,
	DASH_COLORS.blueSoft,
	DASH_COLORS.purple,
	DASH_COLORS.green,
	DASH_COLORS.orange,
	DASH_COLORS.blue,
];
